class TimestampTimer {
  constructor() {
    this.timers = new Map();
    this.timerIdCounter = 0;
    this.isRunning = false;
    this.animationFrameId = null;
    this.isPaused = false;
    this.totalPausedTime = 0;
    this.lastPauseTime = null;
    this.pauseCallbacks = new Set(); // 添加暂停回调
    this.resumeCallbacks = new Set(); // 添加恢复回调
    
    // 用于存储监听器引用，便于清理
    this.visibilityChangeHandler = null;
    this.blurHandler = null;
    this.focusHandler = null;
    this.hasGlobalListeners = false;

    this.init();
  }

  init() {
    // 移除自动添加全局监听器的代码
    // 现在由使用方主动调用 addGlobalListeners()
  }

  // 添加全局监听器
  addGlobalListeners() {
    if (this.hasGlobalListeners || typeof document === "undefined") {
      return;
    }

    console.log("TimestampTimer: 添加全局监听器");

    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        console.log("TimestampTimer: 页面隐藏，触发暂停");
        this.pause();
      } else {
        console.log("TimestampTimer: 页面可见，触发恢复");
        this.resume();
      }
    };

    this.blurHandler = () => {
      console.log("TimestampTimer: 窗口失去焦点，触发暂停");
      this.pause();
    };

    this.focusHandler = () => {
      console.log("TimestampTimer: 窗口获得焦点，触发恢复");
      this.resume();
    };

    document.addEventListener("visibilitychange", this.visibilityChangeHandler);
    window.addEventListener("blur", this.blurHandler);
    window.addEventListener("focus", this.focusHandler);

    this.hasGlobalListeners = true;
  }

  // 移除全局监听器
  removeGlobalListeners() {
    if (!this.hasGlobalListeners || typeof document === "undefined") {
      return;
    }

    console.log("TimestampTimer: 移除全局监听器");

    if (this.visibilityChangeHandler) {
      document.removeEventListener("visibilitychange", this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }

    if (this.blurHandler) {
      window.removeEventListener("blur", this.blurHandler);
      this.blurHandler = null;
    }

    if (this.focusHandler) {
      window.removeEventListener("focus", this.focusHandler);
      this.focusHandler = null;
    }

    this.hasGlobalListeners = false;
  }

  // 添加暂停回调
  onPause(callback) {
    this.pauseCallbacks.add(callback);
    return () => this.pauseCallbacks.delete(callback); // 返回清理函数
  }

  // 添加恢复回调
  onResume(callback) {
    this.resumeCallbacks.add(callback);
    return () => this.resumeCallbacks.delete(callback); // 返回清理函数
  }

  // 触发暂停回调
  triggerPauseCallbacks() {
    this.pauseCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error("Pause callback error:", error);
      }
    });
  }

  // 触发恢复回调
  triggerResumeCallbacks() {
    this.resumeCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error("Resume callback error:", error);
      }
    });
  }

  getEffectiveTime() {
    const now = Date.now();
    if (this.isPaused && this.lastPauseTime) {
      return this.lastPauseTime - this.totalPausedTime;
    }
    return now - this.totalPausedTime;
  }

  pause() {
    if (this.isPaused) return;

    console.log("TimestampTimer: 暂停所有定时器");
    this.isPaused = true;
    this.lastPauseTime = Date.now();

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
      this.isRunning = false;
    }

    // 触发暂停回调
    this.triggerPauseCallbacks();
  }

  resume() {
    if (!this.isPaused) return;

    console.log("TimestampTimer: 恢复所有定时器");

    if (this.lastPauseTime) {
      const pauseDuration = Date.now() - this.lastPauseTime;
      this.totalPausedTime += pauseDuration;
      console.log(
        `本次暂停: ${pauseDuration}ms, 总暂停时间: ${this.totalPausedTime}ms`
      );
    }

    this.isPaused = false;
    this.lastPauseTime = null;

    if (this.timers.size > 0) {
      console.log("重启主循环，当前定时器数量:", this.timers.size);
      this.forceStartMainLoop();
    }

    // 触发恢复回调
    this.triggerResumeCallbacks();
  }

  forceStartMainLoop() {
    this.isRunning = true;
    console.log("TimestampTimer: 强制启动主循环");
    this.mainLoop();
  }

  mainLoop() {
    if (this.isPaused) {
      console.log("主循环检测到暂停状态，停止执行");
      return;
    }

    const effectiveNow = this.getEffectiveTime();
    const timersToRemove = [];

    this.timers.forEach((timer, id) => {
      if (timer.type === "timeout") {
        const elapsed = effectiveNow - timer.startTime;
        if (elapsed >= timer.delay) {
          console.log(`执行 timeout ${id}, elapsed: ${elapsed}ms`);
          try {
            timer.callback();
          } catch (error) {
            console.error("Timer callback error:", error);
          }
          timersToRemove.push(id);
        }
      } else if (timer.type === "interval") {
        const elapsed = effectiveNow - timer.startTime;
        const shouldExecuteCount = Math.floor(elapsed / timer.delay);

        if (shouldExecuteCount > timer.executeCount) {
          const missedExecutions = shouldExecuteCount - timer.executeCount;
          console.log(
            `执行 interval ${id}, 需要执行 ${missedExecutions} 次, 当前计数: ${timer.executeCount}`
          );

          for (let i = 0; i < missedExecutions; i++) {
            try {
              timer.callback();
              timer.executeCount++;
            } catch (error) {
              console.error("Timer callback error:", error);
            }
          }
        }
      }
    });

    timersToRemove.forEach((id) => {
      this.timers.delete(id);
      console.log(`清理已执行的 timeout: ${id}`);
    });

    if (this.timers.size > 0 && !this.isPaused) {
      this.animationFrameId = requestAnimationFrame(() => this.mainLoop());
    } else {
      this.isRunning = false;
      console.log(
        "主循环停止，定时器数量:",
        this.timers.size,
        "暂停状态:",
        this.isPaused
      );
    }
  }

  startMainLoop() {
    if (this.isRunning) {
      console.log("主循环已在运行");
      return;
    }

    this.forceStartMainLoop();
  }

  setTimeout(callback, delay = 0) {
    if (typeof callback !== "function") {
      throw new Error("Callback must be a function");
    }

    const id = ++this.timerIdCounter;
    const timer = {
      id,
      type: "timeout",
      callback,
      delay: Math.max(0, delay),
      startTime: this.getEffectiveTime(),
      created: Date.now(),
    };

    this.timers.set(id, timer);
    console.log(
      `TimestampTimer: setTimeout created, id=${id}, delay=${delay}ms`
    );

    if (!this.isRunning && !this.isPaused) {
      this.startMainLoop();
    }

    return id;
  }

  setInterval(callback, delay = 0) {
    if (typeof callback !== "function") {
      throw new Error("Callback must be a function");
    }

    const id = ++this.timerIdCounter;
    const timer = {
      id,
      type: "interval",
      callback,
      delay: Math.max(10, delay),
      startTime: this.getEffectiveTime(),
      executeCount: 0,
      created: Date.now(),
    };

    this.timers.set(id, timer);
    console.log(
      `TimestampTimer: setInterval created, id=${id}, delay=${delay}ms`
    );

    if (!this.isRunning && !this.isPaused) {
      this.startMainLoop();
    }

    return id;
  }

  clearTimeout(id) {
    if (this.timers.has(id)) {
      const timer = this.timers.get(id);
      this.timers.delete(id);
      console.log(`TimestampTimer: ${timer.type} cleared, id=${id}`);

      if (this.timers.size === 0 && this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
        this.isRunning = false;
      }

      return true;
    }
    return false;
  }

  clearInterval(id) {
    return this.clearTimeout(id);
  }

  clearAll() {
    console.log(`TimestampTimer: 清除所有定时器，共${this.timers.size}个`);
    this.timers.clear();

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.isRunning = false;
    this.isPaused = false;
    this.lastPauseTime = null;
    this.totalPausedTime = 0;

    // 清理回调
    this.pauseCallbacks.clear();
    this.resumeCallbacks.clear();
  }

  getStatus() {
    const effectiveNow = this.getEffectiveTime();

    return {
      totalTimers: this.timers.size,
      isRunning: this.isRunning,
      isPaused: this.isPaused,
      totalPausedTime: this.totalPausedTime,
      effectiveTime: effectiveNow,
      realTime: Date.now(),
      hasGlobalListeners: this.hasGlobalListeners,
      timers: Array.from(this.timers.values()).map((timer) => ({
        id: timer.id,
        type: timer.type,
        delay: timer.delay,
        executeCount: timer.executeCount || 0,
        elapsedTime: effectiveNow - timer.startTime,
        nextExecuteIn:
          timer.type === "timeout"
            ? Math.max(0, timer.delay - (effectiveNow - timer.startTime))
            : timer.delay - ((effectiveNow - timer.startTime) % timer.delay),
      })),
    };
  }

  manualPause() {
    this.pause();
  }

  manualResume() {
    this.resume();
  }
}

// 创建全局实例
const timestampTimer = new TimestampTimer();

// 导出便捷方法
export const tSetTimeout = (callback, delay) =>
  timestampTimer.setTimeout(callback, delay);
export const tSetInterval = (callback, delay) =>
  timestampTimer.setInterval(callback, delay);
export const tClearTimeout = (id) => timestampTimer.clearTimeout(id);
export const tClearInterval = (id) => timestampTimer.clearInterval(id);
export const tClearAll = () => timestampTimer.clearAll();
export const tGetStatus = () => timestampTimer.getStatus();
export const tPause = () => timestampTimer.manualPause();
export const tResume = () => timestampTimer.manualResume();

// 导出回调注册方法
export const tOnPause = (callback) => timestampTimer.onPause(callback);
export const tOnResume = (callback) => timestampTimer.onResume(callback);

// 导出监听器管理方法
export const tAddGlobalListeners = () => timestampTimer.addGlobalListeners();
export const tRemoveGlobalListeners = () => timestampTimer.removeGlobalListeners();

export default TimestampTimer;
